import { Ban, CheckCircle2, RotateCcw, Warehouse } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ErrorState } from '../../../../components/ErrorState';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileBanner } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime, toIso } from '../../../../lib/formatDate';
import { EvidenceStorageLocationModal } from '../components/EvidenceStorageLocationModal';
import { evidenceStorageLocationsApi } from '../evidenceApi';
import type { EvidenceStorageLocationListItem } from '../evidenceTypes';

/** A thin profile — a catalog row has little to say, and an index page is always partnered with one. */
export function EvidenceStorageLocationProfilePage() {
  const { id } = useParams<{ id: string }>();

  const [location, setLocation] = useState<EvidenceStorageLocationListItem | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmingRestore, setConfirmingRestore] = useState(false);
  const [tab, setTab] = useState<'info'>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      evidenceStorageLocationsApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;
          setLocation(next);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;
          setError(caught instanceof ApiError ? caught : new ApiError('Could not load this location.', 0));
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function restore() {
    if (!id) return;

    setRestoring(true);

    try {
      await evidenceStorageLocationsApi.activate(id);
      load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('Could not restore this location.', 0));
    } finally {
      setRestoring(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Evidence Management',
    { label: 'Settings', to: '/evidence/settings' },
    { label: 'Storage Locations', to: '/evidence/settings/storage-locations' },
    location?.name ?? 'Location',
  ];

  if (error && !location) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} correlationId={error.correlationId} onRetry={() => load()} />
      </div>
    );
  }

  if (!location) {
    return (
      <div className="flex h-full min-h-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-3 p-4">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="evidence-storage-location-profile"
        header={
          <ProfileHeader
            id="evidence-storage-location-profile-header"
            icon={<Warehouse className="h-6 w-6" aria-hidden="true" />}
            title={location.name}
            meta={[location.code]}
            status={<StatusText tone={location.isActive ? 'active' : 'inactive'}>{location.isActive ? 'Active' : 'Deleted'}</StatusText>}
            createdBy={location.createdByName}
            createdAt={location.createdAt}
          />
        }
        banner={
          location.isActive ? (
            <ProfileBanner
              id="evidence-storage-location-profile-banner"
              icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}
              message="This location is offered as a place to keep evidence."
            />
          ) : (
            <ProfileBanner
              id="evidence-storage-location-profile-banner"
              icon={<Ban className="h-4 w-4" aria-hidden="true" />}
              message="This location is deleted. It is no longer offered as a place to keep evidence; its code is kept for good."
              action={
                <PermissionGate permission="evidence-storage-locations.delete">
                  <Button
                    id="evidence-storage-location-profile-restore"
                    variant="secondary"
                    leading={<RotateCcw className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => setConfirmingRestore(true)}
                    disabled={restoring}
                    loading={restoring}
                    loadingLabel="Restoring"
                  >
                    Restore
                  </Button>
                </PermissionGate>
              }
            />
          )
        }
        tabs={[{ key: 'info', label: 'Info' }]}
        activeTab={tab}
        onTabChange={setTab}
      >
        <ProfileCard
          id="evidence-storage-location-profile-details"
          title="Details"
          menuItems={[
            { key: 'edit', label: 'Edit', permission: 'evidence-storage-locations.update', onClick: () => setEditing(true) },
          ]}
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Code', value: location.code, mono: true },
              { label: 'Name', value: location.name },
              { label: 'Description', value: location.description },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="evidence-storage-location-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: location.createdByName },
              {
                label: 'Created',
                value: (
                  <time dateTime={toIso(location.createdAt)} className="font-mono">
                    {formatDateTime(location.createdAt)}
                  </time>
                ),
              },
              { label: 'Updated by', value: location.updatedByName },
              {
                label: 'Updated',
                value: location.updatedAt ? (
                  <time dateTime={toIso(location.updatedAt)} className="font-mono">
                    {formatDateTime(location.updatedAt)}
                  </time>
                ) : null,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {/* A banner action that WRITES confirms — no password: a restore is the undo. */}
      {confirmingRestore ? (
        <ConfirmDialog
          open
          onClose={() => setConfirmingRestore(false)}
          onConfirm={() => {
            setConfirmingRestore(false);
            void restore();
          }}
          title="Restore storage location"
          requireConfirm
          loading={restoring}
          id="evidence-storage-location-profile-restore-dialog"
          body={
            <p>
              &quot;{location.name}&quot; is offered again as a place to keep evidence. A restore is
              refused when a live location has taken its name meanwhile.
            </p>
          }
        />
      ) : null}

      {editing ? (
        <EvidenceStorageLocationModal
          editing={location}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
